import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation, useRTL } from '../utils/translations';

interface PackageDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: () => void;
  packageName: string;
  description: string;
  accessories: { name: string; img: string }[];
  isDarkMode: boolean;
  isSelected?: boolean;
}

export const PackageDetailsDialog: React.FC<PackageDetailsDialogProps> = ({
  isOpen,
  onClose,
  onApply,
  packageName,
  description,
  accessories,
  isDarkMode,
  isSelected = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useTranslation();
  const { isRTL } = useRTL();

  // Reset the carousel every time a (possibly different) package's dialog
  // opens, so it starts from the first item rather than wherever a
  // previous package's browsing left off.
  useEffect(() => {
    if (isOpen) setCurrentIndex(0);
  }, [isOpen, packageName]);

  // Clamp defensively at render time too: the effect above only fires after
  // commit, but the parent can re-render this component with a shorter
  // accessories array (switching from a 5-item package to a 3-item one)
  // before that happens - reading accessories[currentIndex] with the stale
  // index would then crash on the very frame the switch occurs.
  const safeIndex = accessories.length ? Math.min(currentIndex, accessories.length - 1) : 0;
  const currentAccessory = accessories[safeIndex];

  const handlePrev = () => setCurrentIndex(prev => (prev > 0 ? prev - 1 : accessories.length - 1));
  const handleNext = () => setCurrentIndex(prev => (prev < accessories.length - 1 ? prev + 1 : 0));

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/[0.88] z-[110]"
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className={cn(
              "fixed left-[4%] right-[4%] top-1/2 -translate-y-1/2 md:left-1/2 md:right-auto md:top-1/2 md:-translate-y-1/2 md:-translate-x-1/2 w-[92%] md:w-full md:max-w-[678px] rounded-[var(--brand-dialog-radius)] shadow-2xl z-[111] overflow-hidden flex flex-col max-h-[90vh]",
              isDarkMode ? "bg-[#111111] text-white" : "bg-white text-black"
            )}
          >
            {/* Header */}
            <div className="px-6 md:px-9 pt-6 md:pt-[35px] pb-4 md:pb-[38px] relative flex-shrink-0">
              <button 
                onClick={onClose}
                className={cn(
                  "absolute top-[18px] right-[24px] size-[34px] rounded-full border flex items-center justify-center transition-colors",
                  isDarkMode 
                    ? "border-white/10 text-white hover:bg-white/10" 
                    : "border-[#e2e2e2] text-[#282828] hover:bg-gray-100"
                )}
              >
                <X className="w-[14px] h-[14px]" />
              </button>

              <h2 className={cn(
                'font-sans text-[20px] md:text-[24px] font-bold mb-2 md:mb-[7px] pr-12',
                isDarkMode ? "text-white" : "text-[#282828]"
              )}>{packageName}</h2>
              <p className={cn(
                'font-sans text-[13px] md:text-[14px] leading-relaxed max-w-[588px]',
                isDarkMode ? "text-white/60" : "text-[#333334]"
              )}>
                {description}
              </p>
            </div>

            {/* Carousel Content */}
            <div className="px-6 md:px-9 pb-4 overflow-y-auto no-scrollbar flex-1 min-h-0">
              <h3 className={cn(
                'font-sans text-[16px] font-bold mb-3',
                isDarkMode ? "text-white" : "text-[#282828]"
              )}>{t('staticTranslations.packageModal.includes', 'Includes')}</h3>
              
              <div className={cn(
                "rounded-[var(--brand-card-radius-lg)] p-2.5",
                isDarkMode ? "bg-white/5" : "bg-[#eaeaea] border-[1.2px] border-[#e2e2e2]"
              )}>
                {/* Image Area */}
                <div className={cn(
                  "rounded-[var(--brand-card-radius)] overflow-hidden h-[160px] md:h-[206px]",
                  isDarkMode ? "bg-[#1A1A1A]" : "bg-white border-[1.2px] border-[#c4c4c4]"
                )}>
                  <AnimatePresence mode="wait">
                    {currentAccessory && (
                      <motion.img
                        key={safeIndex}
                        src={currentAccessory.img}
                        alt={currentAccessory.name}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </AnimatePresence>
                </div>

                {/* Name — centered below image */}
                <p className={cn(
                  'font-sans text-[16px] font-bold text-center mt-[18px]',
                  isDarkMode ? "text-white" : "text-[#282828]"
                )}>
                  {currentAccessory?.name ?? ''}
                </p>

                {/* Nav controls — centered row below name */}
                <div className="flex items-center justify-center gap-[14px] mt-[14px] pb-[24px]">
                  <button
                    onClick={handlePrev}
                    className="size-[28px] rounded-full bg-[var(--brand-accent)] text-white flex items-center justify-center hover:bg-[var(--brand-accent-hover)] transition-all"
                  >
                    {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                  </button>
                  <span className={cn(
                    "text-[14px] min-w-[40px] text-center",
                    isDarkMode ? "text-white/40" : "text-[#4e4e4e]"
                  )}>
                    {safeIndex + 1} {t('staticTranslations.packageModal.of', 'of')} {accessories.length}
                  </span>
                  <button
                    onClick={handleNext}
                    className="size-[28px] rounded-full bg-[var(--brand-accent)] text-white flex items-center justify-center hover:bg-[var(--brand-accent-hover)] transition-all"
                  >
                    {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className={cn(
              "px-6 md:px-9 py-4 md:py-0 md:h-[77px] flex flex-col md:flex-row items-center justify-end gap-3 flex-shrink-0",
              isDarkMode ? "bg-[#111111]" : "bg-[#eaeaea]"
            )}>
              <button
                onClick={onApply}
                className={cn(
                  'order-1 md:order-2 w-full md:w-[206px] md:h-[49px] px-6 py-3 rounded-[var(--brand-button-radius-sm)] text-[16px] md:text-[18px] font-sans font-semibold text-white transition-all',
                  isSelected
                    ? "bg-[var(--brand-accent-secondary)] hover:opacity-80"
                    : "bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)]"
                )}
              >
                {isSelected
                  ? t('staticTranslations.common.remove', 'Remove')
                  : t('staticTranslations.applyChanges', 'Apply Changes')}
              </button>
              <button
                onClick={onClose}
                className={cn(
                  "md:hidden order-2 w-full px-12 py-3.5 rounded-[var(--brand-cta-radius)] text-[14px] font-bold border transition-all",
                  isDarkMode 
                    ? "border-white/20 text-white hover:bg-white/10" 
                    : "border-[var(--brand-accent)] text-[var(--brand-accent)] bg-white hover:bg-blue-50"
                )}
              >
                {t('staticTranslations.common.cancel', 'Cancel')}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
